// Долната лента на листа „Инвентаризация" (както при „Доставка"): първата
// пречка пред записа и действията — при нова: Изтрий черновата · Запази
// временно · Потвърди инвентаризацията (Ctrl/Cmd+Enter); при редакция: Отказ.
// Сумите на десктоп са в реда „Общо" на решетката; тук — само при карти и в
// същия тих вид: „Стойност −22.92 (с ДДС −27.50)" и
// „Излишъци +1.00 · Липси −23.92". Пречката за ед. цена изброява продуктите
// („Въведете ед. цена за: …").
import { useTranslation } from "react-i18next";
import { useInventoryShell } from "../../components/shell/InventoryShellContext";
import { useViewport } from "../../../../hooks/useViewport";
import { KbdChip } from "../../components/KbdChip";
import { PRIMARY_BUTTON_CLASS } from "../../components/list/buttonStyles";
import { DraftFooterActions, type DraftFooterProps } from "../../components/drafts/DraftWorksheetParts";
import { DRAFT_TEXTS } from "../../components/drafts/draftTexts";
import { formatSignedMoney, grossDiffers, signedToneClass } from "./stocktakeMeta";

type Props = {
  mode: "new" | "edit";
  totalNet: number;
  totalGross: number;
  // нетото на излишъците / липсите (само при карти)
  surplusNet: number;
  shortageNet: number;
  issue: string | null;
  // „Въведете поне един преброен" е подсказка (кехлибарено), другото — пречка
  hasCounted: boolean;
  // черновата на нов документ (components/drafts/DraftFooterActions)
  draft: DraftFooterProps;
  saving: boolean;
  onCancel: () => void;
  onSave: () => void;
};

export const StocktakeWorksheetFooter = ({
  mode,
  totalNet,
  totalGross,
  surplusNet,
  shortageNet,
  issue,
  hasCounted,
  saving,
  draft,
  onCancel,
  onSave,
}: Props) => {
  const { t } = useTranslation();
  const { isNarrow } = useInventoryShell();
  const { isDesktop } = useViewport();
  // „Излишъци +1.00" — сумата е в тона на разликата
  const part = (label: string, value: number, toneClass: string) => (
    <span className="whitespace-nowrap">
      {label} <b className={`font-sheet font-bold ${toneClass}`}>{formatSignedMoney(value)}</b>
    </span>
  );
  return (
    <footer
      className={`flex shrink-0 flex-wrap items-center border-t border-shade-2 bg-panel ${
        isNarrow
          ? "gap-3 px-3.5 py-3 pb-[calc(12px+env(safe-area-inset-bottom,0px)*0.5)]"
          : "gap-6 px-6 py-3.5"
      }`}
    >
      {!isDesktop && (
        <>
          <span className="flex flex-wrap items-baseline gap-x-[7px]">
            <span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted">
              {t("inventoryPage.colValue")}
            </span>
            <span className={`font-sheet text-xl font-bold tabular-nums ${signedToneClass(totalNet)}`}>
              {formatSignedMoney(totalNet)}
            </span>
            {grossDiffers(totalNet, totalGross) && (
              <span className="font-sheet text-[12px] tabular-nums text-muted">
                {t("inventoryPage.stqTotalsWithVat", { value: formatSignedMoney(totalGross) })}
              </span>
            )}
          </span>
          <span className="text-[12.5px] text-body">
            {part(t("inventoryPage.stkTotalsSurplus"), surplusNet, "text-ok-deep")}
            {" · "}
            {part(t("inventoryPage.stkTotalsShortage"), shortageNet, "text-danger-ink")}
          </span>
        </>
      )}
      {issue && (
        <span
          className={`text-[12.5px] font-semibold ${
            hasCounted ? "text-danger-ink" : "text-brand-deep-ink"
          }`}
        >
          {issue}
        </span>
      )}
      <div className="flex-1" />
      <DraftFooterActions mode={mode} texts={DRAFT_TEXTS.stocktake} onCancel={onCancel} {...draft} />
      <button
        type="button"
        onClick={onSave}
        disabled={saving}
        className={`${PRIMARY_BUTTON_CLASS} !px-[22px] !py-3 !text-sm ${
          hasCounted ? "" : "opacity-55"
        } ${isNarrow ? "flex-[1_1_100%]" : ""}`}
      >
        {mode === "edit"
          ? t("inventoryPage.editSubmitStocktake")
          : t("inventoryPage.stocktakeSubmit")}
        {!isNarrow && <KbdChip>Ctrl+Enter</KbdChip>}
      </button>
    </footer>
  );
};

export default StocktakeWorksheetFooter;
